import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SplitBar } from '@/components/primitives';
import styles from './RankBars.module.css';

export interface RankBarItem {
  key: string;
  label: string;
  icon?: ReactNode;
  value: number;
  /** Formatted value shown at the bar's end. */
  display: string;
  /** This platform / the picked region — drawn gold. */
  highlight?: boolean;
  /** Row links here (peer platforms → their profile). */
  href?: string;
}

export interface RankBarsProps {
  /** Already in display order. */
  items: RankBarItem[];
  /** Bar colour for every non-highlighted row (a token). Defaults to neutral. */
  color?: string;
  /** Show "#n" before each label. */
  ranked?: boolean;
}

/**
 * Horizontal bars, one per row, scaled to the largest |value|. Used for peer
 * ranks (this platform gold, others neutral) and region comparisons.
 */
export function RankBars({ items, color = 'var(--border-strong)', ranked = true }: RankBarsProps) {
  const max = items.reduce((m, i) => Math.max(m, Math.abs(i.value)), 0) || 1;
  return (
    <ol className={styles.list}>
      {items.map((item, idx) => {
        const label = (
          <>
            {ranked && <span className={styles.rank}>#{idx + 1}</span>}
            {item.icon}
            <span className={styles.name}>{item.label}</span>
          </>
        );
        return (
          <li key={item.key} className={styles.row} data-highlight={item.highlight || undefined}>
            {item.href && !item.highlight ? (
              <Link to={item.href} className={styles.label}>
                {label}
              </Link>
            ) : (
              <span className={styles.label}>{label}</span>
            )}
            <SplitBar
              size="md"
              extent={Math.abs(item.value) / max}
              segments={[{ fraction: 1, color: item.highlight ? 'var(--accent-secondary)' : color }]}
            />
            <span className={styles.value}>{item.display}</span>
          </li>
        );
      })}
    </ol>
  );
}
